import { useCallback, useState } from 'react';

import FilesService from '@/shared/services/filesService';
import { downloadFileLink } from '@/utils/common';

import { IProvider } from '../types/Provider.type';
import { getMoveTemplateType } from '../utils/utils';

interface IFormData {
  provider: IProvider | null;
  movimentationType: string | null;
  templateDownloaded: boolean;
}

interface IUseDownloadTemplate {
  loading: boolean;
  canDownload: (formData: IFormData) => boolean;
  downloadTemplate: (formData: IFormData) => Promise<boolean>;
}

const filesService = new FilesService();

/**
 * Hook responsável por baixar o template de movimentação a partir do endpoint
 * autenticado `GET /templates/:type/download` do sov-v2-files.
 *
 * O `type` é resolvido internamente a partir do provider + tipo de movimentação
 * (nunca vem de input livre do cliente), a resposta é tratada como binária (Blob)
 * e o download é disparado no navegador. O 401 é tratado globalmente pelo
 * interceptor de auth (modal-login).
 */
const useDownloadTemplate = (): IUseDownloadTemplate => {
  const [loading, setLoading] = useState<boolean>(false);

  const canDownload = useCallback((formData: IFormData): boolean => {
    return getMoveTemplateType(formData.provider, formData.movimentationType) !== null;
  }, []);

  const downloadTemplate = useCallback(async (formData: IFormData): Promise<boolean> => {
    const type = getMoveTemplateType(formData.provider, formData.movimentationType);

    if (!type) {
      return false;
    }

    setLoading(true);

    try {
      const { blob, fileName } = await filesService.downloadTemplate(type, `${type}.xls`);

      const blobUrl = URL.createObjectURL(blob);
      downloadFileLink(blobUrl, fileName ?? `${type}.xls`);
      URL.revokeObjectURL(blobUrl);

      return true;
    } catch (error) {
      console.error(error);

      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    canDownload,
    downloadTemplate,
  };
};

export default useDownloadTemplate;
